import { useEffect, useRef } from 'react';
import { supabase } from '../../../src/lib/supabase';
import { idDoEventoFatura } from './realtimeFaturas';

interface Opcoes {
  /** Só assina com usuário resolvido. Vira o nome do canal. */
  usuarioId?: string | null;
  /** `true` segura o lote: uma carga cheia em voo devolveria uma foto mais velha por cima do patch. */
  pausado: boolean;
  /** Mesma pergunta, respondida na hora (ref): entre `setState` e o próximo render o
   *  `pausado` ainda vale o valor antigo, e um patch aplicado nessa brecha seria apagado. */
  pausadoAgora?: () => boolean;
  /** Recarrega só estas faturas. */
  aoAplicar: (ids: number[]) => Promise<void> | void;
  /** Saída de emergência: lote grande demais, payload sem id ou reconexão. */
  aoRecarregarTudo: () => void;
  janelaMs?: number;
  limiteLote?: number;
}

/**
 * Assina `public.faturas` para o Financeiro refletir mudanças sem F5 — da própria
 * Janela de Cobrança, do cálculo em outra janela, do robô, do n8n ou de outro analista.
 *
 * Mesmas decisões de views/CRM/Kanban/useNegociacoesRealtime.ts:
 * - o payload é ignorado e a linha é recarregada por id: um UPDATE não traz colunas
 *   TOAST inalteradas, e aplicar o payload cru apagaria esses campos da tela;
 * - a rajada de uma edição em massa vira uma requisição só (janela de agrupamento);
 * - reconexão recarrega tudo, porque o que mudou com o socket caído se perdeu.
 */
export const useFaturasRealtime = ({
  usuarioId,
  pausado,
  pausadoAgora,
  aoAplicar,
  aoRecarregarTudo,
  janelaMs = 400,
  limiteLote = 500,
}: Opcoes) => {
  // Callbacks por ref: mudam a cada render e não podem derrubar o canal.
  const aoAplicarRef = useRef(aoAplicar);
  aoAplicarRef.current = aoAplicar;
  const aoRecarregarTudoRef = useRef(aoRecarregarTudo);
  aoRecarregarTudoRef.current = aoRecarregarTudo;
  const pausadoRef = useRef(pausado);
  pausadoRef.current = pausado;
  const pausadoAgoraRef = useRef(pausadoAgora);
  pausadoAgoraRef.current = pausadoAgora;

  const buffer = useRef<Set<number>>(new Set());
  const forcarCargaCheia = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const descarregar = useRef(() => {});
  descarregar.current = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    // O effect de `pausado` chama de novo ao destravar.
    if (pausadoRef.current || pausadoAgoraRef.current?.()) return;
    const ids = Array.from(buffer.current);
    if (!ids.length && !forcarCargaCheia.current) return;
    buffer.current.clear();

    if (forcarCargaCheia.current || ids.length > limiteLote) {
      forcarCargaCheia.current = false;
      aoRecarregarTudoRef.current();
      return;
    }
    Promise.resolve(aoAplicarRef.current(ids)).catch((err) =>
      console.error('Realtime faturas — falha ao aplicar lote:', err));
  };

  const agendar = useRef(() => {});
  agendar.current = () => {
    if (timer.current) return;
    timer.current = setTimeout(() => descarregar.current(), janelaMs);
  };

  useEffect(() => {
    if (!usuarioId) return;
    let jaConectou = false;

    const canal = supabase
      .channel(`financeiro-faturas-${usuarioId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'faturas' }, (payload) => {
        const id = idDoEventoFatura(payload as any);
        if (id === null) forcarCargaCheia.current = true;
        else buffer.current.add(id);
        agendar.current();
      })
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (jaConectou) {
          forcarCargaCheia.current = true;
          agendar.current();
        }
        jaConectou = true;
      });

    return () => {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      supabase.removeChannel(canal);
    };
    // `usuarioId` (string) e não o objeto `user`: a referência muda a cada refresh
    // de token e recriaria o canal — e cada recriação vira uma carga cheia.
  }, [usuarioId]);

  useEffect(() => {
    if (!pausado) descarregar.current();
  }, [pausado]);

  // Ações da própria tela entram no mesmo lote: assim também esperam a carga cheia
  // em voo em vez de serem sobrescritas pela foto mais velha que ela vai devolver.
  const enfileirar = useRef((ids: number[]) => {
    ids.forEach((id) => { if (Number.isFinite(Number(id))) buffer.current.add(Number(id)); });
    descarregar.current();
  });

  return { enfileirar: enfileirar.current };
};
